import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import api from '@/lib/axios';
import { tokenManager } from '@/lib/tokenManager';

const router = createRouter({
  history: createWebHistory(),
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (to, from, next) => {
  const authRequired = to.matched.some(r => r.meta?.authRequired);
  const requiredRoles = to.matched.map(r => r.meta?.roles).find(r => !!r);
  const verificationRequired = to.matched.some(r => r.meta?.verificationRequired);

  // 1. Restore Session (If Page Refresh)
  // If we have no memory token, but we have user data, try to refresh from cookie
  let token = tokenManager.getToken();
  const userStr = localStorage.getItem("user");
  let user = userStr ? JSON.parse(userStr) : null;

  // console.log("Router Guard Check:", { to: to.name, token: !!token, user: !!user });

  if (!token && user) {
    // console.log("Attempting session restore...");
    try {

      const { data } = await api.post('/api/refresh-token');
      const newToken = data.data?.token || data.token;
      
      if (newToken) {
        // console.log("Session restored successfully!");
        tokenManager.setToken(newToken);
        token = newToken;
      }
    } catch (e) {
      // Refresh failed - User is truly logged out
      console.warn("Session restore failed:", e);
      tokenManager.clearToken();
      localStorage.removeItem('user');
      user = null;
      if (authRequired) {
        return next({ name: "login", query: { redirectFrom: to.fullPath } });
      }
    }
  }

  // 2. Basic Auth Check
  if (authRequired && !token) {
    // console.log("Auth required and no token - redirecting to login");
    return next({ name: "login", query: { redirectFrom: to.fullPath } });
  }

  // 3. User & Role Validation (If Logged In)
  if (token && user) {
    // A. Data Freshness (Runs ONLY on Page Refresh/First Load)
    if (from.name === undefined) {
      try {
        // We let the axios interceptor inject the token automatically now
        const [verifyRes, roleRes] = await Promise.all([
          api.get("/api/check-verification"),
          api.get("/api/check-roles")
        ]);

        user.email_verified = verifyRes.data.email_verified;
        user.account_verified = verifyRes.data.account_verified;
        user.email_verification_required = !verifyRes.data.email_verified;
        user.roles = roleRes.data.roles || [];

        localStorage.setItem("user", JSON.stringify(user));
      } catch (e) {
        console.error("Session validation failed:", e);
        // If 401, the interceptor likely already handled redirect, but just in case:
        if (e.response && e.response.status === 401) {
          return next({ name: "login" });
        }
      }
    }

    // B. Role Check
    if (requiredRoles) {
      const hasRole = user.roles && user.roles.some(r => requiredRoles.includes(r));
      if (!hasRole) return next({ name: "forbidden" });
    }

    // C. Verification Checks
    if (verificationRequired) {
      if (!user.email_verified) {
        if (to.name !== 'verify-email') return next({ name: "verify-email" });
      } else if (user.account_verified === null) {
        if (to.name !== 'waiting-verification') return next({ name: "waiting-verification" });
      } else if (user.account_verified === 0) {
        if (to.name !== 'update-profile') return next({ name: "update-profile" });
      }
    }

    // D. Redirect AWAY from verification pages if already verified
    const verificationPages = ['verify-email', 'waiting-verification', 'update-profile'];
    if (verificationPages.includes(to.name)) {
      if (user.email_verified && user.account_verified === 1) {
        return next({ name: "dashboard" });
      }
    }
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  next();
});

export default router;
